<script setup lang="ts">
/**
 * One table of scheduled jobs — the rows the old Jobs tab showed, now used
 * three times on the Data sources page: under each source, for the platform's
 * own housekeeping, and for anything nobody has described yet.
 *
 * Read-only on purpose. The backend identity holds `Reader` and nothing
 * more: "triggering ingest from a monitoring page is how you get a 3am
 * surprise" (app/admin_jobs.py).
 */
import { ref } from "vue";
import type { JobRow } from "../api/admin";

const props = withDefaults(
  defineProps<{ jobs: JobRow[]; emptyText?: string }>(),
  { emptyText: "No scheduled jobs." },
);

const expanded = ref<Set<string>>(new Set());

function toggle(name: string) {
  if (expanded.value.has(name)) expanded.value.delete(name);
  else expanded.value.add(name);
  expanded.value = new Set(expanded.value);
}

function when(ts: string): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

function statusClass(s: string): string {
  const v = s.toLowerCase();
  if (v === "failed") return "badge--failed";
  if (v === "succeeded") return "badge--ok";
  if (v === "running") return "badge--running";
  return "";
}
</script>

<template>
  <table class="admin-table">
    <thead>
      <tr>
        <th></th>
        <th>Job</th>
        <th>Schedule</th>
        <th>Last run</th>
        <th>Status</th>
        <th>Assessment</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <template v-for="j in props.jobs" :key="j.name">
        <tr :class="j.overdue && 'row--overdue'">
          <td>
            <span
              class="dot"
              :class="
                j.overdue
                  ? 'dot--bad'
                  : j.last_status.toLowerCase() === 'failed'
                    ? 'dot--warn'
                    : 'dot--ok'
              "
              :title="j.overdue ? 'Overdue' : j.last_status"
            />
          </td>
          <td class="mono small">{{ j.name }}</td>
          <td>
            <code v-if="j.cron" class="small">{{ j.cron }}</code>
            <span v-else class="muted small">{{ j.trigger_type }}</span>
          </td>
          <td class="small">{{ when(j.last_start) }}</td>
          <td>
            <span
              v-if="j.last_status"
              class="badge"
              :class="statusClass(j.last_status)"
            >
              {{ j.last_status }}
            </span>
            <span v-else class="muted">never</span>
          </td>
          <td class="small">
            <strong v-if="j.overdue">{{ j.reason }}</strong>
            <span v-else class="muted">{{ j.reason }}</span>
          </td>
          <td>
            <button
              v-if="j.recent.length"
              class="link"
              :aria-expanded="expanded.has(j.name)"
              :aria-label="`Run history for ${j.name}`"
              @click="toggle(j.name)"
            >
              {{ expanded.has(j.name) ? "hide" : "history" }}
            </button>
          </td>
        </tr>
        <tr v-if="expanded.has(j.name)" class="job-detail">
          <td colspan="7">
            <p v-if="j.recent_failures" class="muted small">
              {{ j.recent_failures }} of the last {{ j.recent.length }} runs
              failed.
            </p>
            <table class="inner">
              <tr v-for="e in j.recent" :key="e.name">
                <td class="mono small">{{ when(e.start) }}</td>
                <td>
                  <span class="badge" :class="statusClass(e.status)">{{
                    e.status
                  }}</span>
                </td>
                <td class="mono small muted">{{ e.name }}</td>
              </tr>
            </table>
          </td>
        </tr></template
      >
    </tbody>
  </table>
</template>

<style scoped>
.row--overdue {
  background: var(--color-error-bg);
}
/* Shape + title carry the meaning too — colour alone is not a signal. */
.dot {
  display: inline-block;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--color-muted);
}
.dot--ok {
  background: var(--color-success);
}
.dot--warn {
  background: var(--color-warn);
}
.dot--bad {
  background: var(--color-error);
}
.badge {
  display: inline-block;
  padding: 0.05rem 0.45rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
}
.badge--failed {
  background: var(--color-error-bg);
  color: var(--color-error);
}
.badge--ok {
  background: var(--color-success-bg);
  color: var(--color-success);
}
.badge--running {
  background: rgb(21 101 192 / 0.12);
  color: #1565c0;
}
.inner {
  border-collapse: collapse;
}
.inner td {
  padding: 0.15rem 0.6rem 0.15rem 0;
}
.job-detail {
  background: var(--color-surface-alt);
}
.mono {
  font-family: var(--font-mono, monospace);
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
}
</style>
